import Seo from "@/components/Seo";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, ExternalLink, Clock, Globe2, Briefcase, Newspaper, GraduationCap, Wrench, TrendingUp, CheckCircle2, Users, Building2, Star } from "lucide-react";
import api from "@/lib/api";
import { TABS, DIFFICULTIES, PAY_RANGES, REGIONS, TIMES, KNOWN_COURSE_CATEGORIES, PLATFORMS, REALITY_CHECK } from "@/lib/sideHustles";

const DIFF_STYLE = {
  beginner: "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30",
  intermediate: "bg-[#3B82F6]/15 text-[#60a5fa] border-[#3B82F6]/30",
  expert: "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30",
};
const primaryDiff = (d) => d.diffKeys[d.diffKeys.length - 1];
const selCls = "bg-[#0e1424] border border-white/10 rounded-md px-3 py-2 text-sm text-[#e2e8f0] focus:border-[#00F0FF] focus:outline-none";

function ToolPills({ tools, noneNote, toolNames }) {
  if ((!tools || tools.length === 0) && noneNote) {
    return <span className="text-xs text-[#64748B] italic" data-testid="tools-none">{noneNote}</span>;
  }
  const known = (name) => {
    const nl = name.toLowerCase().trim();
    return toolNames.some((tn) => tn.includes(nl));
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {tools.map((t) => (
        known(t)
          ? <Link key={t} to={`/tools?search=${encodeURIComponent(t)}`} data-testid={`tool-pill-${t}`}
              className="text-[11px] px-2 py-0.5 rounded-full bg-[#00F0FF]/12 text-[#00F0FF] border border-[#00F0FF]/25 hover:bg-[#00F0FF]/20 transition">{t}</Link>
          : <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8] border border-white/10">{t}</span>
      ))}
    </div>
  );
}

function CoursePills({ courses }) {
  if (!courses || courses.length === 0) {
    return <span className="text-xs text-[#64748B] italic" data-testid="courses-none">None required</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {courses.map((c) => {
        const isCat = KNOWN_COURSE_CATEGORIES.includes(c);
        const to = isCat ? `/courses?category=${encodeURIComponent(c)}` : `/courses?search=${encodeURIComponent(c)}`;
        return (
          <Link key={c} to={to} data-testid={`course-pill-${c}`}
            className="text-[11px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/12 text-[#a78bfa] border border-[#8B5CF6]/25 hover:bg-[#8B5CF6]/20 transition">{c}</Link>
        );
      })}
    </div>
  );
}

function PlatformCard({ p, i, toolNames, featured }) {
  const dk = primaryDiff(p);
  const isHustle = !p.url; // Tab 5 items have no official URL
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.35, delay: (i % 6) * 0.04 }}
      className={`card-surface rounded-xl p-5 flex flex-col ${featured ? "ring-1 ring-[#F59E0B]/40" : ""}`} data-testid={`hustle-card-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
      {featured && (
        <div className="flex items-center gap-1.5 mb-2.5" data-testid={`featured-ribbon-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
          <span className="inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-[#F59E0B]/12 text-[#fbbf24] border border-[#F59E0B]/30">
            <Star className="w-3 h-3 fill-[#fbbf24]" /> Featured this Month
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display font-bold text-lg leading-snug">{p.name}</h3>
        <span className="shrink-0 inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#22C55E]/15 text-[#4ade80] border border-[#22C55E]/30" data-testid="verified-badge">
          <ShieldCheck className="w-3 h-3" /> Verified
        </span>
      </div>
      <div className="flex items-center flex-wrap gap-2 mt-2">
        <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${DIFF_STYLE[dk]}`}>{p.difficulty}</span>
      </div>
      <p className="font-display font-extrabold text-xl text-[#00F0FF] mt-3">{p.pay}</p>
      <div className="flex flex-col gap-1.5 mt-3 text-xs text-[#94A3B8] font-mono-tech">
        <span className="flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5 shrink-0" /> {p.countries}</span>
        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 shrink-0" /> First $: {p.time}</span>
        {isHustle && <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 shrink-0" /> Find clients: {p.findClients}</span>}
      </div>
      <p className="text-sm text-[#cbd5e1] mt-3 flex-1">{p.desc}</p>
      <div className="mt-3 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/25 px-3 py-2">
        <p className="text-xs text-[#fbbf24] leading-relaxed"><span className="font-bold">Honest caveat:</span> {p.caveat}</p>
      </div>

      <div className="mt-4 space-y-2.5">
        <div>
          <p className="font-mono-tech text-[10px] uppercase text-[#64748B] mb-1.5 flex items-center gap-1"><Wrench className="w-3 h-3" /> Related AI Tools</p>
          <ToolPills tools={p.tools} noneNote={p.noneNote} toolNames={toolNames} />
        </div>
        <div>
          <p className="font-mono-tech text-[10px] uppercase text-[#64748B] mb-1.5 flex items-center gap-1"><GraduationCap className="w-3 h-3" /> Related Courses</p>
          <CoursePills courses={p.courses} />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {!isHustle && (
          <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`apply-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="w-full min-h-[44px] inline-flex items-center justify-center gap-1.5 bg-[#00F0FF] text-[#04121a] font-semibold text-sm px-4 rounded-md hover:brightness-110 transition">
            Apply Now <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <div className="flex gap-2">
          {p.jobsQuery && (
            <Link to={`/jobs?search=${encodeURIComponent(p.jobsQuery)}`} data-testid={`jobs-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 border border-white/15 text-[#94A3B8] text-xs px-3 rounded-md hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition">
              <Briefcase className="w-3.5 h-3.5" /> Find Related Jobs
            </Link>
          )}
          {p.newsQuery && (
            <Link to={`/news?search=${encodeURIComponent(p.newsQuery)}`} data-testid={`news-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 border border-white/15 text-[#94A3B8] text-xs px-3 rounded-md hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition">
              <Newspaper className="w-3.5 h-3.5" /> See AI News
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function SideHustles() {
  const [tab, setTab] = useState("train");
  const [diff, setDiff] = useState("all");
  const [pay, setPay] = useState("all");
  const [region, setRegion] = useState("all");
  const [time, setTime] = useState("all");
  const [toolNames, setToolNames] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/tools", { params: { category: "All", search: "" } })
      .then((r) => setToolNames((r.data || []).map((t) => (t.name || "").toLowerCase()).filter(Boolean)))
      .catch(() => setToolNames([]));
    api.get("/side-hustles/featured")
      .then((r) => setFeatured(r.data.featured || []))
      .catch(() => setFeatured([]));
  }, []);

  const payMatch = (p) => {
    if (pay === "all") return true;
    if (pay === "under20") return p.payMin < 20;
    if (pay === "20to50") return p.payMin <= 50 && p.payMax >= 20;
    if (pay === "50to100") return p.payMin <= 100 && p.payMax >= 50;
    if (pay === "100plus") return p.payMax >= 100;
    return true;
  };

  const filtered = useMemo(() => PLATFORMS.filter((p) => {
    if (p.tab !== tab) return false;
    if (diff !== "all" && !p.diffKeys.includes(diff)) return false;
    if (region !== "all" && !p.regions.includes(region)) return false;
    if (time !== "all" && !p.timeKeys.includes(time)) return false;
    if (!payMatch(p)) return false;
    return true;
  }), [tab, diff, pay, region, time]);

  const activeTab = TABS.find((t) => t.key === tab);

  return (
    <div data-testid="side-hustles-page">
      <Seo
        title="AI Side Hustles 2026 — Verified Platforms & Honest Pay Ranges"
        description="The most honest AI side-hustle guide for 2026 — verified platforms, real pay ranges and candid caveats. No hype, no scams. Direct links to apply."
        canonicalPath="/side-hustles" />

      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-6">
        <p className="font-mono-tech text-xs uppercase tracking-widest text-[#00F0FF] mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Earn with AI</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">AI Side Hustles — Real Opportunities</h1>
        <p className="mt-4 text-[#94A3B8] max-w-3xl">Verified platforms where you can genuinely earn using AI skills — with honest pay ranges, real caveats, and direct links to apply. No hype. No scams.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="side-hustles-stats">
          {[
            { v: "25", l: "Verified Platforms" },
            { v: "5", l: "Categories" },
            { v: "$5 to $200+/hr", l: "Pay range" },
            { v: "Monthly", l: "Updated" },
          ].map((s, idx) => (
            <div key={idx} className="card-surface rounded-xl p-4">
              <div className="font-display font-extrabold text-2xl md:text-3xl text-[#00F0FF]">{s.v}</div>
              <div className="font-mono-tech text-xs text-[#94A3B8] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scam warning banner */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-5 flex items-start gap-4" data-testid="scam-warning-banner">
          <AlertTriangle className="w-7 h-7 text-[#fbbf24] shrink-0 mt-0.5" />
          <div>
            <p className="font-display font-bold text-[#fbbf24] text-base">How to spot AI side hustle scams</p>
            <p className="text-sm text-[#e2d3a3] mt-1.5 leading-relaxed">
              A legitimate platform <span className="font-bold">NEVER charges you to join</span>. If a platform asks for payment upfront, promises $500 per day with no experience, or has no verifiable company address — it is a scam. Every platform on this page is free to join and has been independently verified as legitimate.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8">
        <div className="flex flex-wrap gap-2" data-testid="side-hustles-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`min-h-[44px] px-4 rounded-lg text-sm font-semibold border transition-all ${tab === t.key ? "bg-[#00F0FF] text-[#04121a] border-[#00F0FF]" : "border-white/12 text-[#94A3B8] hover:border-[#00F0FF]/50 hover:text-[#00F0FF]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-[#94A3B8] mt-4 max-w-3xl" data-testid="tab-intro">{activeTab?.intro}</p>
      </div>

      {/* Filter bar */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-5">
        <div className="flex flex-wrap gap-2">
          <select value={diff} onChange={(e) => setDiff(e.target.value)} className={selCls} data-testid="filter-difficulty">{DIFFICULTIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <select value={pay} onChange={(e) => setPay(e.target.value)} className={selCls} data-testid="filter-pay">{PAY_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <select value={region} onChange={(e) => setRegion(e.target.value)} className={selCls} data-testid="filter-region">{REGIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <select value={time} onChange={(e) => setTime(e.target.value)} className={selCls} data-testid="filter-time">{TIMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
      </div>

      {/* Cards grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]" data-testid="hustles-empty">No platforms match these filters. Try broadening your selection.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hustles-grid">
            {filtered.map((p, i) => <PlatformCard key={p.name} p={p} i={i} toolNames={toolNames} featured={featured.includes(p.name)} />)}
          </div>
        )}
      </div>

      {/* Reality check */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6" data-testid="reality-check">
        <h2 className="font-display font-bold text-2xl md:text-3xl flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-[#4ade80]" /> What to realistically expect</h2>
        <p className="text-sm text-[#94A3B8] mt-1.5">Honest income timelines based on verified contributor reports — not social media claims.</p>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[640px]">
            <thead>
              <tr className="text-[#64748B] font-mono-tech text-xs uppercase">
                <th className="py-3 pr-4">Stage</th>
                <th className="py-3 pr-4">Timeline</th>
                <th className="py-3 pr-4">Typical income</th>
                <th className="py-3">What you're doing</th>
              </tr>
            </thead>
            <tbody>
              {REALITY_CHECK.map((r) => (
                <tr key={r.stage} className="border-t border-white/10">
                  <td className="py-4 pr-4 font-display font-bold text-[#e2e8f0]">{r.stage}</td>
                  <td className="py-4 pr-4 text-sm text-[#94A3B8] font-mono-tech whitespace-nowrap">{r.period}</td>
                  <td className="py-4 pr-4 font-display font-bold text-[#00F0FF] whitespace-nowrap">{r.income}</td>
                  <td className="py-4 text-sm text-[#94A3B8]">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5 rounded-lg border border-[#F59E0B]/25 bg-[#F59E0B]/5 px-4 py-3">
          <p className="text-xs text-[#e2d3a3] leading-relaxed" data-testid="reality-check-note">
            The FTC took action against $40 million worth of fake AI income schemes in 2025 to 2026. Social media claims of $300 per day from day one are overwhelmingly false. Real income builds over months not days.
          </p>
        </div>
      </div>

      {/* Cross-sell footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/jobs" data-testid="cross-jobs"
            className="card-surface rounded-xl p-5 flex items-center gap-4 hover:border-[#00F0FF]/50 transition group">
            <Briefcase className="w-8 h-8 text-[#00F0FF] shrink-0" />
            <div>
              <p className="font-display font-bold text-base">Looking for a full-time AI career?</p>
              <p className="text-sm text-[#94A3B8] group-hover:text-[#00F0FF] transition">Browse 130+ live AI jobs globally →</p>
            </div>
          </Link>
          <Link to="/courses" data-testid="cross-courses"
            className="card-surface rounded-xl p-5 flex items-center gap-4 hover:border-[#00F0FF]/50 transition group">
            <GraduationCap className="w-8 h-8 text-[#a78bfa] shrink-0" />
            <div>
              <p className="font-display font-bold text-base">Need to build AI skills first?</p>
              <p className="text-sm text-[#94A3B8] group-hover:text-[#00F0FF] transition">Browse free and paid AI courses →</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Affiliate disclosure */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <p className="text-xs text-[#64748B] leading-relaxed" data-testid="affiliate-disclosure">
            Some links on this page may be affiliate links. If you sign up through our link we may earn a small commission at no extra cost to you. This never influences which platforms we include — we only list platforms independently verified as legitimate. Our editorial integrity is not for sale.
          </p>
        </div>
      </div>
    </div>
  );
}
